import React, { memo, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { useMemoizedFn } from 'ahooks';
// import RangePicker from '@gp/components/RangePickerNew';
import RangePicker from '@gp/components/GeneralRangePicker';
import InfoBubble from '@gp/components/InfoBubble';
import { checkDateRangeDiff } from '../../utils/common';
import { MAX_DIFF_MONTHS } from '../../utils/constants';
import { toFirstUpperCase, getValidDateRange } from '@gp/utils/common';

const RANGE_PICKER_TYPE = 'month';

export interface IFilter {
  date_type: IDateType;
  start_date: string;
  end_date: string;
}

interface IFilterProps extends IBaseProps {
  dateRangeData?: {
    start_date: string;
    end_date: string;
  };
  loading?: boolean;
  filter: IFilter;
  isLimitMaxSelectRange: boolean;
  onFilterChange?: (filter: IFilter) => void;
}

// const RANGE_PICKER_TABS = [{ tabName: 'Month' }];

const Filter: React.FC<IFilterProps> = (props) => {
  const {
    dateRangeData,
    loading = false,
    filter: filterProps,
    isLimitMaxSelectRange = false,
    onFilterChange,
    style,
    className,
    ...remains
  } = props;

  const [filter, setFilter] = useState<IFilter>({
    date_type: 'Month',
    start_date: '',
    end_date: '',
  });

  const [dateType, setDateType] = useState<IDateType>('Month');
  const [dateValue, setDateValue] = useState<string[]>([]);
  const [dateRangeTipsShow, setDateRangeTipsShow] = useState(false);

  // 可选日期范围整理成起始月的第一天，结束月的最后一天
  const dateRange = useMemo(() => {
    const {
      start_date,
      end_date,
    } = dateRangeData || {};
    if (moment(start_date).isValid() && moment(end_date).isValid()) {
      const d1 = moment(start_date).startOf('month').format('YYYY-MM-DD'),
            d2 = moment(end_date).endOf('month').format('YYYY-MM-DD');
      return [d1, d2];
    } else {
      return ['', ''];
    }
  }, [dateRangeData?.start_date, dateRangeData?.end_date]);

  const isRangePickerDisabled = useMemo(() => {
    return !(dateRangeData?.start_date && dateRangeData?.end_date) || loading;
  }, [dateRangeData, loading]);

  /* const disabledDateRange = useMemo<string[]>(() => {
    const startDate = dateRangeData?.start_date;
    if (!startDate) return [];
    const startDateM = moment(startDate);
    if (!startDateM.isValid()) return [];
    const disabledEndDate = startDateM
      .startOf('month')
      .subtract(1, 'days')
      .format('YYYY-MM-DD');
    return ['1970-01-01', disabledEndDate];
  }, [dateRangeData]);

  const endYear = useMemo<string>(() => {
    const endDate = dateRangeData?.end_date;
    const lastMonth = moment()
      .startOf('month')
      .subtract(1, 'months')
      .format('YYYY-MM-DD');
    if (!endDate) return lastMonth;
    const endDateM = moment(endDate);
    return !endDateM.isValid()
      ? lastMonth
      : endDateM.startOf('month').format('YYYY-MM-DD');
  }, [dateRangeData]); */

  const getValidDateRange1 = useMemoizedFn(
    (options: { startDate: moment.Moment; endDate: moment.Moment }) => {
      // const { start_date, end_date } = dateRangeData || {};
      const [start_date, end_date] = dateRange;
      const { startDate, endDate } = options || {};
      const isLimit = !!(start_date && end_date);
      let startDateTemp = startDate;
      let endDateTemp = endDate;
      if (isLimit) {
        startDateTemp =
          moment(startDate).isSameOrAfter(start_date) &&
          moment(startDate).isSameOrBefore(end_date)
            ? startDate
            : moment(start_date);

        endDateTemp =
          moment(endDate).isSameOrAfter(start_date) &&
          moment(endDate).isSameOrBefore(end_date)
            ? endDate
            : moment(end_date);
      }
      return {
        startDate: startDateTemp,
        endDate: endDateTemp,
      };
    },
  );

  const getValidDateRange2 = useMemoizedFn(
    (options: { startDate: moment.Moment; endDate: moment.Moment }) => {
      let { startDate, endDate } = options || {};
      let isOver = false;

      const isRangeValid = checkDateRangeDiff(startDate, endDate);
      // 时间范围跨度大于24个月，重置为 当前时间的上一个月开始，往前推24个月
      if (!isRangeValid) {
        startDate = moment(endDate).subtract(MAX_DIFF_MONTHS - 1, 'months');
        isOver = true;
      }

      return {
        isOver,
        startDate,
        endDate,
      };
    },
  );

  const handleDateChange = useMemoizedFn((startDate, endDate, dateType) => {
    let validStartDateStr = startDate,
        validEndDateStr = endDate;
    // 当有最大可选日期范围限制时
    if (isLimitMaxSelectRange) {
      let startDateTemp = moment(startDate).startOf('month');
      let endDateTemp = moment(endDate).startOf('month');

      const {
        isOver,
        startDate: validStartDate,
        endDate: validEndDate,
      } = getValidDateRange2(
        getValidDateRange1({
          startDate: startDateTemp,
          endDate: endDateTemp,
        }),
      );

      validStartDateStr = validStartDate?.format('YYYY-MM-DD');
      validEndDateStr = validEndDate?.format('YYYY-MM-DD');

      if (isOver) {
        setDateRangeTipsShow(true);
        setTimeout(() => {
          setDateRangeTipsShow(false);
        }, 3000);
      }
    }

    const newFilter = {
      ...filter,
      date_type: dateType,
      start_date: validStartDateStr || '',
      end_date: validEndDateStr || '',
    };
    const newDateValue =
      validStartDateStr && validEndDateStr
        ? [validStartDateStr, validEndDateStr]
        : [];
    setFilter?.(newFilter);
    setDateType(dateType);
    setDateValue(newDateValue);
    onFilterChange?.(newFilter);
  });

  useEffect(() => {
    if (filterProps?.date_type && filterProps?.date_type !== dateType) {
      const newFilter = { ...filter, date_type: filterProps?.date_type };
      setDateType(filterProps?.date_type);
      setFilter(newFilter);
    }

    const startDate = filterProps?.start_date;
    const endDate = filterProps?.end_date;
    const isStartDateDiff = startDate !== dateValue?.[0];
    const isEndDateDiff = endDate !== dateValue?.[1];
    if (startDate && endDate && (isStartDateDiff || isEndDateDiff)) {
      const newDateRange = getValidDateRange(startDate, endDate);
      const newStartDate = newDateRange?.[0] || '';
      const newEndDate = newDateRange?.[1] || '';
      const newFilter = {
        ...filter,
        start_date: newStartDate,
        end_date: newEndDate,
      };
      setDateValue(newDateRange);
      setFilter(newFilter);
    }
  }, [filterProps?.date_type, filterProps?.start_date, filterProps?.end_date]);

  return (
    <div
      {...remains}
      style={style}
      className={classnames('dbg-studio-people-filter', className)}
    >
      <InfoBubble
        className="globalpedia-modal-font14"
        placementOffset={20}
        customBubbleContent={
          dateRangeTipsShow ? 'Can only query up to 24 months of data.' : ''
        }
        DomOrText={
          <>
            {/* @ts-ignore */}
            {/* <RangePicker
              key={disabledDateRange?.[1]}
              loading={false}
              disabled={isRangePickerDisabled}
              range={true}
              headerTabShow={false}
              isAllOfTimeHidden={true}
              tabOptions={RANGE_PICKER_TABS}
              disables={disabledDateRange}
              endYear={endYear}
              dateType={dateType}
              setDateType={setDateType}
              dateValue={dateValue}
              setDateValue={setDateValue}
              onChange={handleDateChange}
            /> */}
            <RangePicker
              type={RANGE_PICKER_TYPE}
              typeList={[RANGE_PICKER_TYPE]}
              value={dateValue}
              onChange={(value: any, type: string) => {
                handleDateChange(value?.[0], value?.[1], toFirstUpperCase(type));
              }}
              disabled={isRangePickerDisabled}
              dateRange={dateRange}
            />
          </>
        }
      />
    </div>
  );
};

export default memo(Filter);
